import React, { createContext, useContext, useReducer, useCallback } from 'react';
import { getAgentMetrics } from '../utils/api';
import toast from '../utils/toastUtils';

// Action types
const ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_AGENT_METRICS: 'SET_AGENT_METRICS',
  INVALIDATE_CACHE: 'INVALIDATE_CACHE',
};

// Initial state
const initialState = {
  agentMetrics: {
    data: null,
    loading: false,
    error: null,
    lastFetched: null,
    isStale: true,
  },
};

// Reducer
const agentDataReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_LOADING:
      return {
        ...state,
        agentMetrics: {
          ...state.agentMetrics,
          loading: action.loading,
          error: action.loading ? null : state.agentMetrics.error,
        },
      };

    case ACTIONS.SET_ERROR:
      return {
        ...state,
        agentMetrics: {
          ...state.agentMetrics,
          loading: false,
          error: action.error,
        },
      };

    case ACTIONS.SET_AGENT_METRICS:
      return {
        ...state,
        agentMetrics: {
          data: action.data,
          loading: false,
          error: null,
          lastFetched: Date.now(),
          isStale: false,
        },
      };

    case ACTIONS.INVALIDATE_CACHE:
      return {
        ...state,
        agentMetrics: {
          ...state.agentMetrics,
          isStale: true,
        },
      };

    default:
      return state;
  }
};

// Context
const AgentDataContext = createContext();

// Cache duration (10 minutes - agents might want longer cache)
const CACHE_DURATION = 10 * 60 * 1000;

// Provider component
export const AgentDataProvider = ({ children }) => {
  const [state, dispatch] = useReducer(agentDataReducer, initialState);

  // Helper function to check if data is stale
  const isDataStale = useCallback((lastFetched) => {
    if (!lastFetched) return true;
    return Date.now() - lastFetched > CACHE_DURATION;
  }, []);

  // Fetch agent metrics with caching
  const fetchAgentMetrics = useCallback(async (forceRefresh = false) => {
    const { agentMetrics } = state;
    
    // Return cached data if fresh and not forcing refresh
    if (!forceRefresh && agentMetrics.data && !agentMetrics.isStale && !isDataStale(agentMetrics.lastFetched)) {
      return agentMetrics.data;
    }

    // Don't fetch if already loading
    if (agentMetrics.loading) return agentMetrics.data;

    dispatch({ type: ACTIONS.SET_LOADING, loading: true });

    try {
      const data = await getAgentMetrics();
      dispatch({ type: ACTIONS.SET_AGENT_METRICS, data });
      return data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch agent metrics';
      dispatch({ type: ACTIONS.SET_ERROR, error: errorMessage });
      toast.error(errorMessage);
      throw error;
    }
  }, [state, isDataStale]);

  // Cache invalidation method
  const invalidateCache = useCallback(() => {
    dispatch({ type: ACTIONS.INVALIDATE_CACHE });
  }, []);

  // Method to call when refresh button is clicked
  const onRefreshClicked = useCallback(async () => {
    invalidateCache();
    // Immediately fetch fresh data
    return await fetchAgentMetrics(true);
  }, [invalidateCache, fetchAgentMetrics]);

  // Method to call when agent performs actions that might affect metrics
  const onAgentActionCompleted = useCallback(() => {
    invalidateCache();
  }, [invalidateCache]);

  const value = {
    // State
    agentMetrics: state.agentMetrics,
    
    // Fetch methods
    fetchAgentMetrics,
    
    // Cache invalidation
    invalidateCache,
    
    // Event handlers for cache invalidation
    onRefreshClicked,
    onAgentActionCompleted,
  };

  return (
    <AgentDataContext.Provider value={value}>
      {children}
    </AgentDataContext.Provider>
  );
};

// Hook to use the context
export const useAgentData = () => {
  const context = useContext(AgentDataContext);
  if (!context) {
    throw new Error('useAgentData must be used within an AgentDataProvider');
  }
  return context;
};

export default AgentDataContext; 